import {AxiosError} from 'axios'
import {ZadImportRowError} from '../../../models/zad'

export interface ZadApiError {
  success: false
  error: string
  errorCode?: string
  status?: number
  errors?: ZadImportRowError[]
  details?: {
    product?: string
    productCode?: string
    productName?: string
    requested?: number
    available?: number
    soldQuantity?: number
    previouslyReturnedQuantity?: number
    alreadyReturned?: number
    returnable?: number
    saleNumber?: string
    refundableCredit?: number
    outstanding?: number
    customer?: string
    customerNumber?: string
  }
}

type LocalizedMessage = {
  en?: string
  fr?: string
  ar?: string
}

export function pickLocalizedMessage(message: unknown, locale: 'en' | 'fr' = 'fr'): string {
  if (!message) return ''
  if (typeof message === 'string') return message
  if (typeof message === 'object') {
    const localized = message as LocalizedMessage
    return localized[locale] || localized.en || localized.fr || ''
  }
  return ''
}

function looksLikeStack(text: string): boolean {
  return /at\s+\S+\s+\(/.test(text) || text.includes('\n    at ')
}

export function parseZadApiError(error: unknown, locale: 'en' | 'fr' = 'fr'): ZadApiError {
  const err = error as AxiosError<any> & {message?: string}
  const status = err.response?.status
  const data = err.response?.data
  const errorCode = typeof data?.errorCode === 'string' ? data.errorCode : undefined
  let message = pickLocalizedMessage(data?.message, locale)

  if (!message && typeof data?.error === 'string') {
    message = data.error
  }

  if (!message && err instanceof Error) {
    message = err.message
  }

  if (message && looksLikeStack(message)) {
    message = ''
  }

  const errors = Array.isArray(data?.errors) ? data.errors : undefined

  if (errorCode === 'DUPLICATE_CODE') {
    return {
      success: false,
      error: 'DUPLICATE_CODE',
      errorCode,
      status: status || 409,
    }
  }

  if (errorCode === 'IMPORT_VALIDATION_ERROR') {
    return {
      success: false,
      error: message || 'The import contains validation errors.',
      errorCode,
      status: status || 400,
      errors: errors || [],
    }
  }

  if (status === 401) {
    message = message || 'Authentication required'
  } else if (status === 403 || errorCode === 'ROLE_FORBIDDEN') {
    message = message || 'Access forbidden'
  } else if (status === 404) {
    message = message || 'Not found'
  } else if (status === 409) {
    message = message || 'Conflict'
  } else if (status === 500 || !status) {
    message = message || 'Server error'
  }

  return {
    success: false,
    error: message || 'Request failed',
    errorCode,
    status,
    errors,
    details: data?.details,
  }
}

export function resolveZadUserMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  if (err.errorCode === 'IMPORT_VALIDATION_ERROR') {
    return {
      i18nId: 'ZAD.IMPORT.VALIDATION_ERROR',
      fallback: 'The import contains validation errors.',
    }
  }
  if (err.errorCode === 'DUPLICATE_BARCODE') {
    return {
      i18nId: 'ZAD.PRODUCTS.DUPLICATE_BARCODE',
      fallback: 'A product with this barcode already exists.',
    }
  }
  if (err.errorCode === 'BARCODE_ALREADY_EXISTS') {
    return {
      i18nId: 'ZAD.PRODUCTS.BARCODE_ALREADY_EXISTS',
      fallback: 'This product already has a barcode.',
    }
  }
  if (err.errorCode === 'SUPPLIER_DOCUMENT_ALREADY_EXISTS') {
    return {
      i18nId: 'ZAD.PURCHASES.IMPORT.DUPLICATE_DOCUMENT',
      fallback: 'This supplier invoice reference has already been imported.',
    }
  }
  if (err.errorCode === 'DUPLICATE_CODE' || (err.status === 409 && !err.errorCode)) {
    return {i18nId: 'ZAD.COMMON.DUPLICATE_CODE', fallback: 'This code is already used.'}
  }
  if (err.errorCode === 'CATEGORY_NOT_FOUND') {
    return {
      i18nId: 'ZAD.PRODUCTS.CATEGORY_NOT_FOUND',
      fallback: 'Product category was not found.',
    }
  }
  if (err.errorCode === 'PRODUCT_TYPE_CATEGORY_MISMATCH') {
    return {
      i18nId: 'ZAD.PRODUCTS.TYPE_CATEGORY_MISMATCH',
      fallback: 'This product type does not belong to the selected category.',
    }
  }
  if (err.errorCode === 'PRODUCT_TYPE_NOT_FOUND') {
    return {
      i18nId: 'ZAD.PRODUCTS.TYPE_NOT_FOUND',
      fallback: 'Product type was not found.',
    }
  }
  if (err.errorCode === 'PRODUCT_TYPE_INACTIVE') {
    return {
      i18nId: 'ZAD.PRODUCTS.TYPE_INACTIVE',
      fallback: 'This product type is inactive.',
    }
  }
  if (err.errorCode === 'CATEGORY_INACTIVE') {
    return {
      i18nId: 'ZAD.PRODUCTS.CATEGORY_INACTIVE',
      fallback: 'This product category is inactive.',
    }
  }
  if (err.errorCode === 'INITIAL_STOCK_ALREADY_EXISTS') {
    return {
      i18nId: 'ZAD.STOCK.INITIAL_EXISTS',
      fallback: 'Initial stock for this product has already been set.',
    }
  }
  if (err.errorCode === 'INSUFFICIENT_STOCK') {
    return {
      i18nId: 'ZAD.STOCK.INSUFFICIENT',
      fallback: 'Insufficient stock for this outbound adjustment.',
    }
  }
  if (err.errorCode === 'PRODUCT_INACTIVE') {
    return {
      i18nId: 'ZAD.STOCK.PRODUCT_INACTIVE',
      fallback: 'This product is inactive.',
    }
  }
  if (err.errorCode === 'MOVEMENT_TYPE_FORBIDDEN') {
    return {
      i18nId: 'ZAD.STOCK.TYPE_FORBIDDEN',
      fallback: 'This movement type cannot be created manually.',
    }
  }
  if (err.errorCode === 'SUPPLIER_NOT_FOUND') {
    return {i18nId: 'ZAD.PURCHASES.SUPPLIER_NOT_FOUND', fallback: 'Supplier was not found.'}
  }
  if (err.errorCode === 'SUPPLIER_INACTIVE') {
    return {i18nId: 'ZAD.PURCHASES.SUPPLIER_INACTIVE', fallback: 'This supplier is inactive.'}
  }
  if (err.errorCode === 'PRODUCT_NOT_FOUND') {
    return {i18nId: 'ZAD.PURCHASES.PRODUCT_NOT_FOUND', fallback: 'Product was not found.'}
  }
  if (err.errorCode === 'PRODUCT_IDENTIFIER_MISMATCH') {
    return {
      i18nId: 'ZAD.PURCHASES.IMPORT.PRODUCT_IDENTIFIER_MISMATCH',
      fallback: 'Product code and barcode do not match the same product.',
    }
  }
  if (err.errorCode === 'DUPLICATE_PRODUCT') {
    return {
      i18nId: 'ZAD.PURCHASES.DUPLICATE_PRODUCT',
      fallback: 'This product is already present in this purchase.',
    }
  }
  if (err.errorCode === 'VALIDATION_ERROR' && /date|startDate|endDate|période|period|range/i.test(err.error || '')) {
    return {
      i18nId: 'ZAD.REPORTS.INVALID_DATE',
      fallback: 'The selected date range is invalid.',
    }
  }
  if (
    err.errorCode === 'INVALID_VAT' ||
    err.errorCode === 'INVALID_VAT_RATE' ||
    (err.errorCode === 'VALIDATION_ERROR' && /vat/i.test(err.error || ''))
  ) {
    return {i18nId: 'ZAD.PURCHASES.VAT_INVALID', fallback: 'The VAT rate is invalid.'}
  }
  if (err.errorCode === 'INVALID_DISCOUNT' || err.errorCode === 'VALIDATION_ERROR') {
    return {i18nId: 'ZAD.PURCHASES.DISCOUNT_INVALID', fallback: 'The discount is invalid.'}
  }
  if (err.errorCode === 'PURCHASE_NOT_EDITABLE') {
    return {i18nId: 'ZAD.PURCHASES.NOT_EDITABLE', fallback: 'This purchase can no longer be edited.'}
  }
  if (err.errorCode === 'PURCHASE_ALREADY_VALIDATED') {
    return {i18nId: 'ZAD.PURCHASES.ALREADY_VALIDATED', fallback: 'This purchase is already validated.'}
  }
  if (err.errorCode === 'PURCHASE_NOT_VALIDATABLE') {
    return {i18nId: 'ZAD.PURCHASES.NOT_VALIDATABLE', fallback: 'This purchase cannot be validated.'}
  }
  if (err.errorCode === 'PURCHASE_NOT_VALIDATED') {
    return {
      i18nId: 'ZAD.PAYMENTS.PURCHASE_NOT_VALIDATED',
      fallback: 'Only validated purchases can receive allocations.',
    }
  }
  if (err.errorCode === 'PURCHASE_SUPPLIER_MISMATCH') {
    return {
      i18nId: 'ZAD.PAYMENTS.PURCHASE_SUPPLIER_MISMATCH',
      fallback: 'This purchase does not belong to the selected supplier.',
    }
  }
  if (err.errorCode === 'DUPLICATE_PURCHASE_ALLOCATION') {
    return {
      i18nId: 'ZAD.PAYMENTS.DUPLICATE_PURCHASE',
      fallback: 'This purchase is already selected.',
    }
  }
  if (err.errorCode === 'INVALID_PAYMENT_AMOUNT') {
    return {
      i18nId: 'ZAD.PAYMENTS.AMOUNT_INVALID',
      fallback: 'Payment amount must be greater than 0.',
    }
  }
  if (err.errorCode === 'INVALID_ALLOCATION_AMOUNT') {
    return {
      i18nId: 'ZAD.PAYMENTS.ALLOCATION_INVALID',
      fallback: 'The allocation amount is invalid.',
    }
  }
  if (err.errorCode === 'PURCHASE_OVERPAYMENT') {
    return {
      i18nId: 'ZAD.PAYMENTS.PURCHASE_OVERPAYMENT',
      fallback: 'This allocation would exceed the purchase outstanding.',
    }
  }
  if (err.errorCode === 'PAYMENT_ALREADY_CANCELLED') {
    return {
      i18nId: 'ZAD.PAYMENTS.ALREADY_CANCELLED',
      fallback: 'This payment is already cancelled.',
    }
  }
  if (err.errorCode === 'CUSTOMER_NOT_FOUND') {
    return {i18nId: 'ZAD.CUSTOMERS.NOT_FOUND', fallback: 'Customer was not found.'}
  }
  if (err.errorCode === 'INVALID_CUSTOMER_TYPE') {
    return {i18nId: 'ZAD.CUSTOMERS.INVALID_TYPE', fallback: 'Invalid customer type.'}
  }
  if (err.errorCode === 'INVALID_MAAREFATI_STUDENT') {
    return {i18nId: 'ZAD.CUSTOMERS.INVALID_STUDENT', fallback: 'Invalid Maarefati student.'}
  }
  if (err.errorCode === 'INVALID_MAAREFATI_PARENT') {
    return {i18nId: 'ZAD.CUSTOMERS.INVALID_PARENT', fallback: 'Invalid Maarefati parent.'}
  }
  if (err.errorCode === 'SALE_NOT_FOUND') {
    return {i18nId: 'ZAD.SALES.NOT_FOUND', fallback: 'Sale was not found.'}
  }
  if (err.errorCode === 'SALE_NOT_DRAFT') {
    return {i18nId: 'ZAD.SALES.NOT_DRAFT', fallback: 'Only draft sales can be changed.'}
  }
  if (err.errorCode === 'SALE_ALREADY_VALIDATED') {
    return {i18nId: 'ZAD.SALES.ALREADY_VALIDATED', fallback: 'This sale is already validated.'}
  }
  if (err.errorCode === 'SALE_CANCELLED') {
    return {i18nId: 'ZAD.SALES.CANCELLED', fallback: 'This sale is cancelled.'}
  }
  if (err.errorCode === 'CUSTOMER_INACTIVE') {
    return {i18nId: 'ZAD.SALES.CUSTOMER_INACTIVE', fallback: 'This customer is inactive.'}
  }
  if (err.errorCode === 'INVALID_QUANTITY') {
    return {i18nId: 'ZAD.SALES.INVALID_QUANTITY', fallback: 'Quantity must be a positive integer.'}
  }
  if (err.errorCode === 'INVALID_UNIT_PRICE') {
    return {i18nId: 'ZAD.SALES.INVALID_PRICE', fallback: 'The unit price is invalid.'}
  }
  if (err.errorCode === 'INVALID_GLOBAL_DISCOUNT') {
    return {i18nId: 'ZAD.SALES.DISCOUNT_INVALID', fallback: 'The global discount is invalid.'}
  }
  if (err.errorCode === 'INSUFFICIENT_STOCK') {
    return {i18nId: 'ZAD.SALES.INSUFFICIENT_STOCK', fallback: 'Insufficient stock for this sale.'}
  }
  if (err.status === 401) {
    return {i18nId: 'ZAD.COMMON.UNAUTHORIZED', fallback: 'Authentication required.'}
  }
  if (err.status === 403 || err.errorCode === 'ROLE_FORBIDDEN') {
    return {i18nId: 'ZAD.COMMON.FORBIDDEN', fallback: 'You do not have access to this resource.'}
  }
  if (err.status === 404) {
    return {i18nId: 'ZAD.COMMON.NOT_FOUND', fallback: 'Resource not found.'}
  }
  if (err.status === 500 || !err.status) {
    return {i18nId: 'ZAD.COMMON.SERVER_ERROR', fallback: 'An unexpected error occurred.'}
  }
  return {i18nId: '', fallback: err.error}
}

export function isZadListForbidden(err: ZadApiError | null | undefined): boolean {
  if (!err) return false
  return err.status === 403 || err.errorCode === 'ROLE_FORBIDDEN'
}
